import React, { useState, useMemo, useEffect } from "react";
import {
  Spinner,
  Dropdown,
  Button,
  Container,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import SubtleBadge from "components/common/SubtleBadge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useUsers from "hooks/userManagement/useUsers";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import IconButton from "components/common/IconButton";
import CustomPaginationFooter from "components/common/CustomPaginationFooter";
import Guard from "components/access-control/rbac/Guard";
import { toast } from "react-toastify";

const Users = () => {
  const navigate = useNavigate();

  const {
    users,
    loading,
    error,
    getUserById,
    refetch,
    deleteUserProfileProperty,
  } = useUsers();

  const formatText = (text) => `${text.slice(0, 8)}...`;

  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const handleViewUser = async (id) => {
    try {
      await getUserById(id);
      navigate(`/olitask/users/${id}`);
    } catch (err) {
      console.error("Error navigating to user details:", err);
    }
  };

  const handleEditUser = async (id) => {
    try {
      await getUserById(id);
      navigate(`/olitask/users/${id}/edit`);
    } catch (err) {
      console.error("Error navigating to user edit page:", err);
    }
  };

  // Open modal on delete click
  const confirmDelete = (id) => {
    setUserToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      await deleteUserProfileProperty(userToDelete);
      toast.success("User profile deleted successfully.");
      refetch();
    } catch (err) {
      console.error("Failed to delete user profile:", err);
      toast.error("Failed to delete user profile.");
    } finally {
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  useEffect(() => {
    const handleFocus = () => {
      refetch();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [refetch]);

  const statusColors = {
    ACTIVE: "success",
    DISABLED: "secondary",
    SUSPENDED: "warning",
    DELETED: "danger",
  };

  const columns = useMemo(
    () => [
      {
        accessorFn: (row) =>
          `${row.firstName || ""} ${row.lastName || ""}`.trim() || "None",
        id: "fullName",
        header: "Name",
        meta: {
          headerProps: { className: "text-900" },
        },
      },
      {
        accessorKey: "id",
        header: "User Id",
        cell: ({ getValue }) => formatText(getValue()) || "None",
        meta: {
          headerProps: { className: "text-900" },
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ getValue }) => formatText(getValue()) || "None",
        meta: {
          headerProps: { className: "text-900" },
        },
      },
      {
        accessorKey: "phone",
        header: "Phone",
        cell: ({ getValue }) => getValue() || "None",
        meta: {
          headerProps: { className: "text-900" },
        },
      },
      {
        accessorKey: "gender",
        header: "Gender",
        meta: {
          headerProps: { className: "text-900" },
        },
        cell: ({ getValue }) => getValue() || "None",
      },

      {
        accessorKey: "status",
        header: "Status",
        meta: {
          headerProps: { className: "text-900" },
        },
        cell: ({ getValue }) => {
          const status = getValue() || "DISABLED";
          const badgeColor = statusColors[status] || "secondary";
          const icon =
            status === "ACTIVE"
              ? "check"
              : status === "SUSPENDED"
              ? "ban"
              : "exclamation-triangle";

          return (
            <SubtleBadge pill bg={badgeColor}>
              {status}
              <FontAwesomeIcon
                icon={icon}
                transform="shrink-2"
                className="ms-1"
              />
            </SubtleBadge>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        meta: {
          headerProps: { className: "text-900" },
        },
        cell: ({ row }) => {
          const item = row.original;
          return (
            <Dropdown className="action-dropdown p-0">
              <Dropdown.Toggle
                as="button"
                className="bg-transparent border-0 p-0 shadow-none text-secondary"
              >
                ⋯
              </Dropdown.Toggle>
              <Dropdown.Menu className="action-menu">
                <Dropdown.Item onClick={() => handleViewUser(item.id)}>
                  View
                </Dropdown.Item>
                <Guard privilege={"UPDATE_USERS"}>
                  <Dropdown.Item onClick={() => handleEditUser(item.id)}>
                    Edit
                  </Dropdown.Item>
                </Guard>

                <Guard privilege={"DELETE_USERS"}>
                  <Dropdown.Divider />
                  <Dropdown.Item className="text-danger">Delete</Dropdown.Item>
                </Guard>
              </Dropdown.Menu>
            </Dropdown>
          );
        },
      },
    ],
    [navigate, users]
  );

  const table = useAdvanceTable({
    data: users,
    columns,
    sortable: true,
    perPage: 10,
    pagination: true,
  });

  if (loading && users.length === 0) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3 text-sm text-success" style={{ fontSize: 13.33 }}>
          Loading users...
        </span>
      </Container>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        className="mt-4 mb-4"
        title="User Profiles"
        icon="users"
        subtitle="User profiles registered on Olitask includes internal users, partners and Customers."
      />

      <AdvanceTableProvider {...table}>
        <Row className="mb-3 g-3">
          <Col md={6} lg={5}>
            <AdvanceTableSearchBox placeholder="Search user profiles..." />
          </Col>
          <Col md={6} lg={7} className="text-end">
            <Guard privilege={"CREATE_USERS"}>
              <IconButton
                variant="falcon-default"
                size="sm"
                icon="plus"
                transform="shrink-3"
                onClick={() => navigate("/olitask/users/add")}
              >
                <span className="d-none d-sm-inline-block ms-1">Add User</span>
              </IconButton>
            </Guard>
          </Col>
        </Row>

        <AdvanceTable
          headerClassName="bg-300 text-nowrap align-middle text-black"
          rowClassName="align-middle white-space-nowrap"
          tableProps={{
            bordered: true,
            striped: true,
            className: "fs-10 mb-0 overflow-hidden",
          }}
        />
        <CustomPaginationFooter
          table={table}
          totalEntries={users.length}
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </AdvanceTableProvider>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this user profile? This action cannot
          be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Users;
